import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import BusinessIcon from "@material-ui/icons/Business";
import FolderIcon from "@material-ui/icons/Folder";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		backgroundColor: theme.palette.type === "dark" ? theme.palette.background.default : "#f7f9fc",
		borderTop: 0,
		padding: theme.spacing(0.5, 0),
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
	clientGroupHeader: {
		alignItems: "center",
		backgroundColor: theme.palette.type === "dark" ? "#1d2637" : "#edf3ff",
		borderBottom: `1px solid ${theme.palette.type === "dark" ? "#334155" : "#d8e3f7"}`,
		borderTop: `1px solid ${theme.palette.type === "dark" ? "#334155" : "#d8e3f7"}`,
		color: theme.palette.text.primary,
		display: "flex",
		fontSize: 12,
		fontWeight: 800,
		justifyContent: "space-between",
		lineHeight: "normal",
		marginTop: theme.spacing(0.5),
		minHeight: 42,
		padding: theme.spacing(0, 1),
	},
	clientGroupHeaderUnread: {
		backgroundColor: theme.palette.type === "dark" ? "#263453" : "#e4edff",
		boxShadow: "inset 3px 0 0 #3f51b5",
	},
	clientGroupIdentity: {
		alignItems: "center",
		display: "flex",
		gap: theme.spacing(0.75),
		minWidth: 0,
	},
	clientGroupName: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	clientGroupCount: {
		color: theme.palette.type === "dark" ? "#a8b8d2" : "#58709b",
		fontSize: 11,
		fontWeight: 700,
		marginLeft: theme.spacing(0.5),
	},
	clientGroupUnreadBadge: {
		backgroundColor: "#e53935",
		borderRadius: 10,
		color: "#fff",
		fontSize: 10,
		fontWeight: 800,
		lineHeight: "18px",
		marginLeft: theme.spacing(0.5),
		minWidth: 18,
		padding: "0 6px",
		textAlign: "center",
		whiteSpace: "nowrap",
	},
	clientGroupIcon: {
		color: theme.palette.type === "dark" ? "#9eb5ff" : "#3154b5",
		fontSize: 17,
	},
	groupToggle: {
		padding: 4,
	},
}));

const mergeContact = (currentContact, incomingContact) => {
	if (!incomingContact) return currentContact;
	if (!currentContact) return incomingContact;

	const mergedContact = { ...currentContact, ...incomingContact };
	["internalGroupLink", "gnsOsClientLink"].forEach(linkName => {
		if (!Object.prototype.hasOwnProperty.call(incomingContact, linkName)) {
			mergedContact[linkName] = currentContact[linkName];
		}
	});

	return mergedContact;
};

const mergeTicket = (currentTicket, incomingTicket) => ({
	...currentTicket,
	...incomingTicket,
	contact: mergeContact(currentTicket?.contact, incomingTicket?.contact)
});

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				const mergedTicket = mergeTicket(state[ticketIndex], ticket);
				state[ticketIndex] = mergedTicket;
				if (mergedTicket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = mergeTicket(state[ticketIndex], ticket);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = mergeTicket(state[ticketIndex], ticket);
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		return state.map(ticket => ticket.contactId === contact.id
			? { ...ticket, contact: mergeContact(ticket.contact, contact) }
			: ticket);
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

	const TicketsList = (props) => {
	const { status, searchParam, showAll, selectedQueueIds, priority, groupMode, updateCount, style, collapseGroupsByDefault = true } =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const [collapsedGroups, setCollapsedGroups] = useState({});
	const { user } = useContext(AuthContext);
	const pageSize = ["client", "group"].includes(groupMode) ? 1000 : undefined;

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, selectedQueueIds, priority, pageSize]);

	useEffect(() => {
		setCollapsedGroups({});
	}, [groupMode]);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(selectedQueueIds),
		priority,
		pageSize,
	});

	useEffect(() => {
		if (!status && !searchParam) return;
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		const socket = openSocket();

		const shouldUpdateTicket = ticket => !searchParam &&
			(!ticket.userId || ticket.userId === user?.id || showAll) &&
			(!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1) &&
			(!priority || priority === "all" || ticket.priority === priority);

		const notBelongsToUserQueues = ticket =>
			ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

		const notMatchesPriority = ticket =>
			priority && priority !== "all" && ticket.priority !== priority;

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		socket.on("ticket", data => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			if (data.action === "update" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: data.ticket,
				});
			}

			if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "update" && notMatchesPriority(data.ticket)) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		});

		socket.on("appMessage", data => {
			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: data.ticket,
				});
			}
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		const handleLocalContactUpdate = event => {
			dispatch({
				type: "UPDATE_TICKET_CONTACT",
				payload: event.detail
			});
		};
		window.addEventListener("whaticket:contact-updated", handleLocalContactUpdate);

		return () => {
			window.removeEventListener("whaticket:contact-updated", handleLocalContactUpdate);
			socket.disconnect();
		};
	}, [status, searchParam, showAll, user, selectedQueueIds, priority]);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	const ticketGroups = ticketsList.reduce((groups, ticket) => {
		const internalLink = ticket.contact?.internalGroupLink;
		const clientLink = ticket.contact?.gnsOsClientLink;
		const isCombinedGroup = groupMode === "group";
		const useInternalGroup = isCombinedGroup && Boolean(internalLink?.internalGroupId);
		const useClientGroup = !useInternalGroup && Boolean(clientLink?.gnsOsClientId);
		const groupType = useInternalGroup ? "internal" : useClientGroup ? "client" : "unclassified";
		const groupId = useInternalGroup ? internalLink.internalGroupId : clientLink?.gnsOsClientId;
		const key = groupType === "unclassified" ? "unclassified" : `${groupType}-${groupId}`;
		if (!groups[key]) {
			groups[key] = {
				key,
				name: useInternalGroup
					? internalLink.internalGroup?.name
					: useClientGroup
						? clientLink.gnsOsClientName
						: isCombinedGroup ? "Sin clasificar" : "Sin vincular",
				type: groupType,
				tickets: []
			};
		}
		groups[key].tickets.push(ticket);
		return groups;
	}, {});

	Object.values(ticketGroups).forEach(group => {
		group.unreadTickets = group.tickets.filter(ticket => ticket.unreadMessages > 0).length;
	});

	const renderTicketGroups = () => Object.values(ticketGroups)
		.sort((a, b) => {
			const priority = { internal: 0, client: 1, unclassified: 2 };
			return priority[a.type] - priority[b.type] || a.name.localeCompare(b.name);
		})
		.map(group => {
		const isCollapsed = collapseGroupsByDefault
			? collapsedGroups[group.key] !== false
			: Boolean(collapsedGroups[group.key]);
		return (
			<React.Fragment key={group.key}>
				<ListSubheader
					component="div"
					disableSticky
					className={`${classes.clientGroupHeader}${group.unreadTickets > 0 ? ` ${classes.clientGroupHeaderUnread}` : ""}`}
				>
					<span className={classes.clientGroupIdentity}>
						{group.type === "internal"
							? <FolderIcon className={classes.clientGroupIcon} />
							: group.type === "client"
								? <BusinessIcon className={classes.clientGroupIcon} />
								: <LinkOffIcon className={classes.clientGroupIcon} />}
						<span className={classes.clientGroupName}>{group.name}</span>
						<span className={classes.clientGroupCount}>{group.tickets.length} {group.tickets.length === 1 ? "chat" : "chats"}</span>
						{group.unreadTickets > 0 && (
							<span className={classes.clientGroupUnreadBadge}>
								{group.unreadTickets} {group.unreadTickets === 1 ? "nuevo" : "nuevos"}
							</span>
						)}
					</span>
					<IconButton
						className={classes.groupToggle}
						size="small"
						aria-label={isCollapsed ? `Expandir ${group.name}` : `Contraer ${group.name}`}
						onClick={() => setCollapsedGroups(current => ({ ...current, [group.key]: !isCollapsed }))}
					>
						{isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
					</IconButton>
				</ListSubheader>
				{!isCollapsed && group.tickets.map(ticket => <TicketListItem ticket={ticket} key={ticket.id} />)}
			</React.Fragment>
		);
	});

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{groupMode !== "none"
								? renderTicketGroups()
								: ticketsList.map(ticket => <TicketListItem ticket={ticket} key={ticket.id} />)}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
